import React, { useState } from "react";
import { Upload } from "lucide-react";

interface UploadTemplateFormProps {
  onSubmit: (file: File, name: string) => void;
  onCancel: () => void;
  uploading: boolean;
}

const UploadTemplateForm: React.FC<UploadTemplateFormProps> = ({
  onSubmit,
  onCancel,
  uploading,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setName(selectedFile.name.split(".")[0]); // Use filename as default name

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (file && name.trim()) {
      onSubmit(file, name.trim());
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* File Upload */}
      <div>
        <label
          className="block text-sm font-medium text-gray-700 mb-2"
          dir="rtl"
        >
          בחר תמונת תבנית
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            id="template-file-upload"
          />
          <label htmlFor="template-file-upload" className="cursor-pointer">
            {preview ? (
              <div className="space-y-2">
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-32 mx-auto rounded"
                />
                <p className="text-sm text-gray-600">{file?.name}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                <p className="text-sm text-gray-600" dir="rtl">
                  לחץ לבחירת קובץ
                </p>
              </div>
            )}
          </label>
        </div>
      </div>

      {/* Name Input */}
      <div>
        <label
          className="block text-sm font-medium text-gray-700 mb-2"
          dir="rtl"
        >
          שם התבנית
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
          placeholder="הכנס שם לתבנית"
          dir="rtl"
          required
        />
      </div>

      {/* Buttons */}
      <div className="flex space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          dir="rtl"
        >
          ביטול
        </button>
        <button
          type="submit"
          disabled={!file || !name.trim() || uploading}
          className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          dir="rtl"
        >
          {uploading ? "מעלה..." : "העלה תבנית"}
        </button>
      </div>
    </form>
  );
};

export default UploadTemplateForm;
